/**
 * Email Agent Constants
 *
 * Centralized configuration values for email agent processing.
 * These values can be overridden per-agent in email_agents.config.
 */

/**
 * Maximum age of emails to process (in days).
 * Emails older than this will be skipped to prevent processing old backlog.
 * This is a safety measure when starting fresh or syncing new inboxes.
 */
export const EMAIL_MAX_AGE_DAYS = 7;

/**
 * Maximum number of concurrent workers for fan-out processing.
 * Limits parallel API calls to prevent overwhelming external services.
 */
export const MAX_CONCURRENT_WORKERS = 10;

/**
 * Rate limiting delay between worker dispatches (in milliseconds).
 * Small delay to prevent bursting all requests at once.
 */
export const DISPATCH_DELAY_MS = 100;

/**
 * Timeout for dispatch confirmation (in milliseconds).
 * Dispatcher waits this long for worker response before moving on.
 * Workers continue processing independently after this timeout.
 */
export const DISPATCH_CONFIRM_TIMEOUT_MS = 15_000; // 15 seconds

/**
 * Timeout for fire-and-forget dispatch (in milliseconds).
 * Short timeout since workers return 202 immediately.
 * Prevents hanging requests from blocking the batch.
 */
export const DISPATCH_SEND_TIMEOUT_MS = 3_000; // 3 seconds

/**
 * Default email processing limits per run.
 */
export const EMAIL_LIMITS = {
  /** Max emails to sync per inbox per run */
  SYNC_PER_INBOX: 50,
  /** Max emails to classify per agent per run */
  CLASSIFY_PER_AGENT: 50,
  /** Max CRM opportunities to create per agent per run */
  CRM_PER_AGENT: 50,
  /** Max auto-replies to process per agent per run */
  AUTO_REPLY_PER_AGENT: 20,
  /** Max chars of email body sent to LLM for classification */
  CLASSIFY_BODY_MAX_CHARS: 4000,
} as const;

/**
 * Concurrency limits for internal operations within workers.
 */
export const INTERNAL_CONCURRENCY = {
  /** Concurrent email classification requests */
  CLASSIFY: 3,
  /** Concurrent CRM API calls */
  CRM: 1,
  /** Concurrent auto-reply processing */
  AUTO_REPLY: 1,
} as const;

/**
 * Rate limiting delays for external API calls (in milliseconds).
 */
export const API_DELAYS = {
  /** Delay between email sync calls */
  SYNC: 300,
  /** Delay between CRM API calls */
  CRM: 500,
  /** Delay between auto-reply sends */
  AUTO_REPLY: 1000,
} as const;

/**
 * Gmail label defaults pre-filled on a new agent, and the UI's starting colors.
 * Colors come from Gmail's own palette (the swatches under AI Reply → Post-Reply Actions).
 * Labels are only ever applied to messages the agent actually sends — see
 * `needsGmailModify` in the auto-reply service.
 */
export const DEFAULT_GMAIL_LABELS = {
  /** Applied to inbound emails the AI has processed. */
  incoming: {
    name: "Versafy Read",
    backgroundColor: "#4a86e8",
    textColor: "#ffffff",
  },
  /** Applied to the AI-generated replies. */
  outgoing: {
    name: "Versafy Responded",
    backgroundColor: "#16a766",
    textColor: "#ffffff",
  },
} as const;
